/**
 * FrameIoFolderPicker - Cascading folder selector for Frame.io
 *
 * Now with:
 * - Default upload folder (skips slow folder loading)
 * - "Use different location" toggle for custom folder selection
 * - Cached folders from database (fast!) with fallback to API
 *
 * Usage:
 * <FrameIoFolderPicker
 *   value={selectedFolderId}
 *   onChange={(folderId, folderPath) => setSelectedFolderId(folderId)}
 *   showSourceOption={true}  // Show "same as source" option
 * />
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Alert,
  Button
} from '@mui/material';
import {
  Folder as FolderIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { BACKEND_URL } from '../utils/apiConfig';
import { authFetch } from '../utils/authHelpers';
import { leesJson } from '../utils/apiFout';

// Default upload folder - Full Frame SRT Output folder
// This folder is used when user doesn't select a specific location
const DEFAULT_FOLDER = {
  id: '194b7168-9722-402c-a97d-d77a8cfd492a', // Full Frame project root_asset_id (NOT project ID!)
  name: 'Full Frame (standaard)',
  path: 'Full Frame / SRT Output'
};

const FrameIoFolderPicker = ({
  value,
  onChange,
  showSourceOption = true,
  hasFrameIoSource = false, // True if video comes from Frame.io (then "naast bronvideo" makes sense)
  disabled = false,
  size = 'small'
}) => {
  // Mode: 'default' = use default folder, 'custom' = show folder picker
  const [mode, setMode] = useState('default');

  // State for folder data
  const [folders, setFolders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Cascading selection state
  const [selectedClient, setSelectedClient] = useState(null);      // Level 1: Client/Project
  const [selectedFolder, setSelectedFolder] = useState(null);       // Level 2: Folder
  const [selectedSubfolder, setSelectedSubfolder] = useState(null); // Level 3: Subfolder
  const [selectedDeepFolder, setSelectedDeepFolder] = useState(null); // Level 4: Deep folder

  // Computed current path
  const [currentPath, setCurrentPath] = useState('');

  // Check cache status on mount
  useEffect(() => {
    const checkCache = async () => {
      try {
        const response = await authFetch(`${BACKEND_URL}/Api/frameio/cache-status`);
        const data = await leesJson(response, 'Frame.io cache-status laden');
        setCacheStatus(data);
      } catch (err) {
        // bewust stil: cache-status is alleen een informatief badge; de mappenlijst zelf meldt fouten via setError.
        console.warn('[FrameIoFolderPicker] Cache status check failed:', err.message);
      }
    };
    checkCache();
  }, []);

  // Set default folder on mount (for default mode)
  useEffect(() => {
    if (mode === 'default' && onChange) {
      if (hasFrameIoSource) {
        // Video from Frame.io: upload next to source video
        onChange(null, 'Naast bronvideo (standaard)');
      } else {
        // Local upload: use default Full Frame folder
        onChange(DEFAULT_FOLDER.id, DEFAULT_FOLDER.path);
      }
    }
  }, [mode, hasFrameIoSource]); // Only on mode/source change, not onChange to prevent infinite loop

  // Fetch folders from API (cached or direct)
  const fetchFolders = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      // Try cached endpoint first (much faster)
      const endpoint = forceRefresh
        ? `${BACKEND_URL}/Api/frameio/folders` // Force fresh from API
        : `${BACKEND_URL}/Api/frameio/folders-cached`; // Use cache

      console.log(`[FrameIoFolderPicker] Fetching folders from: ${endpoint}`);
      const response = await authFetch(endpoint);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setFolders(data);
      console.log('[FrameIoFolderPicker] Loaded folders:', data?.folders?.length || 0, 'clients', data.cached ? '(cached)' : '(fresh)');
    } catch (err) {
      console.error('[FrameIoFolderPicker] Error loading folders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync/refresh folders (tries AI Engine cache, falls back to direct API)
  const syncFolders = async () => {
    setSyncing(true);
    setError(null);
    try {
      console.log('[FrameIoFolderPicker] Syncing folders...');
      const response = await authFetch(`${BACKEND_URL}/Api/frameio/folders/sync`, {
        method: 'POST'
      });
      if (!response.ok) {
        throw new Error(`Sync failed: ${response.status}`);
      }
      const data = await response.json();
      console.log(`[FrameIoFolderPicker] Sync complete: ${data.folder_count} folders (cached: ${data.cached !== false})`);

      // Reload folders (will use cache or direct API)
      await fetchFolders();

      // Update cache status
      setCacheStatus({
        available: data.cached !== false,
        lastSync: new Date().toISOString(),
        folderCount: data.folder_count
      });
    } catch (err) {
      console.error('[FrameIoFolderPicker] Sync error:', err);
      // Even if sync fails, try to load folders directly
      console.log('[FrameIoFolderPicker] Trying direct folder fetch after sync failure...');
      try {
        await fetchFolders();
      } catch (fetchErr) {
        setError(`Laden mislukt: ${fetchErr.message}`);
      }
    } finally {
      setSyncing(false);
    }
  };

  // Fetch children for a specific folder (lazy loading)
  const fetchChildrenForFolder = useCallback(async (folderId, folderName) => {
    if (!folderId) return [];

    setLoadingChildren(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/frameio/folder/${folderId}/children`);
      if (!response.ok) {
        console.warn(`[FrameIoFolderPicker] Could not fetch children for ${folderName}`);
        return [];
      }
      const children = await response.json();
      console.log(`[FrameIoFolderPicker] Loaded ${children.length} children for ${folderName}`);
      return children;
    } catch (err) {
      console.error(`[FrameIoFolderPicker] Error fetching children for ${folderName}:`, err);
      return [];
    } finally {
      setLoadingChildren(false);
    }
  }, []);

  // Load folders when switching to custom mode
  useEffect(() => {
    if (mode === 'custom' && !folders) {
      fetchFolders();
    }
  }, [mode, folders, fetchFolders]);

  // Get children of selected level
  const getClientFolders = () => selectedClient?.children || [];
  const getSubfolders = () => selectedFolder?.children || [];
  const getDeepFolders = () => selectedSubfolder?.children || [];

  // Update the final selected folder ID when any level changes
  useEffect(() => {
    if (mode !== 'custom') return; // Don't update in default mode

    // Determine the deepest selected folder
    let finalFolder = null;
    let path = '';

    if (selectedDeepFolder) {
      finalFolder = selectedDeepFolder;
      path = `${selectedClient?.name} / ${selectedFolder?.name} / ${selectedSubfolder?.name} / ${selectedDeepFolder?.name}`;
    } else if (selectedSubfolder) {
      finalFolder = selectedSubfolder;
      path = `${selectedClient?.name} / ${selectedFolder?.name} / ${selectedSubfolder?.name}`;
    } else if (selectedFolder) {
      finalFolder = selectedFolder;
      path = `${selectedClient?.name} / ${selectedFolder?.name}`;
    } else if (selectedClient) {
      finalFolder = selectedClient;
      path = selectedClient?.name;
    }

    setCurrentPath(path);

    // Only call onChange if we have a final selection
    if (finalFolder && onChange) {
      onChange(finalFolder.id, path);
    }
  }, [selectedClient, selectedFolder, selectedSubfolder, selectedDeepFolder, onChange, mode]);

  // Handle mode toggle
  const handleModeChange = (event) => {
    const newMode = event.target.checked ? 'custom' : 'default';
    setMode(newMode);

    // Always reset selections when toggling mode
    setSelectedClient(null);
    setSelectedFolder(null);
    setSelectedSubfolder(null);
    setSelectedDeepFolder(null);
    setCurrentPath('');

    if (newMode === 'default') {
      if (onChange) onChange(null, 'Naast bronvideo (standaard)');
    } else {
      // Force reload folders when switching to custom mode
      setFolders(null);
    }
  };

  // Handle client selection
  const handleClientChange = async (e) => {
    const clientId = e.target.value;
    if (!clientId) {
      setSelectedClient(null);
      setSelectedFolder(null);
      setSelectedSubfolder(null);
      setSelectedDeepFolder(null);
      if (onChange) onChange(null, '');
      return;
    }

    let client = folders?.folders?.find(f => f.id === clientId);

    // If client has no children loaded, try to fetch them
    if (client && (!client.children || client.children.length === 0)) {
      console.log(`[FrameIoFolderPicker] Client ${client.name} has no children, fetching...`);
      const children = await fetchChildrenForFolder(client.id, client.name);
      if (children.length > 0) {
        // Update client with children
        client = { ...client, children };
        // Also update in folders state so it persists
        setFolders(prev => ({
          ...prev,
          folders: prev.folders.map(f => f.id === clientId ? client : f)
        }));
      }
    }

    setSelectedClient(client);
    setSelectedFolder(null);
    setSelectedSubfolder(null);
    setSelectedDeepFolder(null);
  };

  // Handle folder selection (Level 2)
  const handleFolderChange = async (e) => {
    const folderId = e.target.value;
    if (!folderId) {
      setSelectedFolder(null);
      setSelectedSubfolder(null);
      setSelectedDeepFolder(null);
      return;
    }

    let folder = getClientFolders().find(f => f.id === folderId);

    // Lazy load children if not present
    if (folder && (!folder.children || folder.children.length === 0)) {
      console.log(`[FrameIoFolderPicker] Folder ${folder.name} has no children, fetching...`);
      const children = await fetchChildrenForFolder(folder.id, folder.name);
      if (children.length > 0) {
        folder = { ...folder, children };
        // Update in parent's children array
        if (selectedClient) {
          const updatedChildren = selectedClient.children.map(f => f.id === folderId ? folder : f);
          const updatedClient = { ...selectedClient, children: updatedChildren };
          setSelectedClient(updatedClient);
          setFolders(prev => ({
            ...prev,
            folders: prev.folders.map(f => f.id === selectedClient.id ? updatedClient : f)
          }));
        }
      }
    }

    setSelectedFolder(folder);
    setSelectedSubfolder(null);
    setSelectedDeepFolder(null);
  };

  // Handle subfolder selection (Level 3)
  const handleSubfolderChange = async (e) => {
    const subfolderId = e.target.value;
    if (!subfolderId) {
      setSelectedSubfolder(null);
      setSelectedDeepFolder(null);
      return;
    }

    let subfolder = getSubfolders().find(f => f.id === subfolderId);

    // Lazy load children if not present
    if (subfolder && (!subfolder.children || subfolder.children.length === 0)) {
      console.log(`[FrameIoFolderPicker] Subfolder ${subfolder.name} has no children, fetching...`);
      const children = await fetchChildrenForFolder(subfolder.id, subfolder.name);
      if (children.length > 0) {
        subfolder = { ...subfolder, children };
      }
    }

    setSelectedSubfolder(subfolder);
    setSelectedDeepFolder(null);
  };

  // Handle deep folder selection
  const handleDeepFolderChange = (e) => {
    const deepFolderId = e.target.value;
    if (!deepFolderId) {
      setSelectedDeepFolder(null);
      return;
    }

    const deepFolder = getDeepFolders().find(f => f.id === deepFolderId);
    setSelectedDeepFolder(deepFolder);
  };

  // Clear all selections
  const handleClear = () => {
    setSelectedClient(null);
    setSelectedFolder(null);
    setSelectedSubfolder(null);
    setSelectedDeepFolder(null);
    if (onChange) onChange(null, '');
  };

  // Format last sync time
  const formatLastSync = (dateStr) => {
    if (!dateStr) return 'Nooit';
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    if (diffHours < 1) return 'Zojuist';
    if (diffHours < 24) return `${diffHours} uur geleden`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} dag${diffDays > 1 ? 'en' : ''} geleden`;
  };

  // DEFAULT MODE UI
  if (mode === 'default') {
    const defaultLabel = hasFrameIoSource ? 'Naast bronvideo' : DEFAULT_FOLDER.path;
    const defaultHint = hasFrameIoSource
      ? 'Output komt automatisch naast de bronvideo in Frame.io'
      : `Output komt in: ${DEFAULT_FOLDER.path}`;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<FolderIcon />}
            label={defaultLabel}
            color="primary"
            variant="outlined"
            size="small"
          />
          <FormControlLabel
            control={
              <Switch
                checked={mode === 'custom'}
                onChange={handleModeChange}
                size="small"
                disabled={disabled}
              />
            }
            label={<Typography variant="body2" color="textSecondary">Andere locatie kiezen</Typography>}
          />
        </Box>
        {showSourceOption && (
          <Typography variant="caption" color="textSecondary">
            {defaultHint}
          </Typography>
        )}
      </Box>
    );
  }

  // CUSTOM MODE - Show folder picker

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControlLabel
          control={<Switch checked={true} onChange={handleModeChange} size="small" />}
          label={<Typography variant="body2">Andere locatie kiezen</Typography>}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">
            Frame.io mappen laden...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControlLabel
          control={<Switch checked={true} onChange={handleModeChange} size="small" />}
          label={<Typography variant="body2">Andere locatie kiezen</Typography>}
        />
        <Alert severity="error" sx={{ py: 0.5 }}>
          Fout bij laden: {error}
          <IconButton size="small" onClick={() => fetchFolders(true)}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Alert>
      </Box>
    );
  }

  // No folders
  if (!folders || !folders.folders || folders.folders.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControlLabel
          control={<Switch checked={true} onChange={handleModeChange} size="small" />}
          label={<Typography variant="body2">Andere locatie kiezen</Typography>}
        />
        <Alert severity="info" sx={{ py: 0.5 }}>
          Geen mappen in cache.
          <Button
            size="small"
            startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
            onClick={syncFolders}
            disabled={syncing}
            sx={{ ml: 1 }}
          >
            {syncing ? 'Synchroniseren...' : 'Synchroniseer nu'}
          </Button>
        </Alert>
      </Box>
    );
  }

  const clientFolders = getClientFolders();
  const subfolders = getSubfolders();
  const deepFolders = getDeepFolders();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Mode toggle + cache info */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <FormControlLabel
          control={<Switch checked={true} onChange={handleModeChange} size="small" disabled={disabled} />}
          label={<Typography variant="body2">Andere locatie kiezen</Typography>}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {folders.cached && (
            <Typography variant="caption" color="textSecondary">
              Cache: {formatLastSync(cacheStatus?.lastSync)}
            </Typography>
          )}
          <Tooltip title="Vernieuw folder cache">
            <IconButton
              size="small"
              onClick={syncFolders}
              disabled={syncing}
            >
              {syncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Breadcrumb / Current Path */}
      {currentPath && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<FolderIcon />}
            label={currentPath}
            size="small"
            onDelete={handleClear}
            deleteIcon={<CloseIcon />}
            sx={{ maxWidth: 400 }}
          />
        </Box>
      )}

      {/* Cascading Selectors */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        {/* Level 1: Client/Project */}
        <FormControl size={size} sx={{ minWidth: 180 }} disabled={disabled}>
          <InputLabel>Klant</InputLabel>
          <Select
            value={selectedClient?.id || ''}
            label="Klant"
            onChange={handleClientChange}
            MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
          >
            <MenuItem value="">
              <em>Selecteer klant...</em>
            </MenuItem>
            {folders.folders.map(client => (
              <MenuItem key={client.id} value={client.id}>
                {client.name}
                {client.children?.length > 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                    ({client.children.length})
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Level 2: Folder (Project binnen klant) */}
        {selectedClient && (
          <>
            <ChevronRightIcon sx={{ alignSelf: 'center', color: 'text.secondary' }} />
            {loadingChildren && clientFolders.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="textSecondary">Mappen laden...</Typography>
              </Box>
            ) : clientFolders.length > 0 ? (
              <FormControl size={size} sx={{ minWidth: 180 }} disabled={disabled || loadingChildren}>
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedFolder?.id || ''}
                  label="Project"
                  onChange={handleFolderChange}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                >
                  <MenuItem value="">
                    <em>Klant root</em>
                  </MenuItem>
                  {clientFolders.map(folder => (
                    <MenuItem key={folder.id} value={folder.id}>
                      {folder.name}
                      {folder.children?.length > 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                          ({folder.children.length})
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ alignSelf: 'center' }}>
                (geen submappen)
              </Typography>
            )}
          </>
        )}

        {/* Level 3: Subfolder */}
        {selectedFolder && subfolders.length > 0 && (
          <>
            <ChevronRightIcon sx={{ alignSelf: 'center', color: 'text.secondary' }} />
            <FormControl size={size} sx={{ minWidth: 180 }} disabled={disabled}>
              <InputLabel>Submap</InputLabel>
              <Select
                value={selectedSubfolder?.id || ''}
                label="Submap"
                onChange={handleSubfolderChange}
                MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
              >
                <MenuItem value="">
                  <em>Huidige map</em>
                </MenuItem>
                {subfolders.map(subfolder => (
                  <MenuItem key={subfolder.id} value={subfolder.id}>
                    {subfolder.name}
                    {subfolder.children?.length > 0 && (
                      <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                        ({subfolder.children.length})
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {/* Level 4: Deep folder */}
        {selectedSubfolder && deepFolders.length > 0 && (
          <>
            <ChevronRightIcon sx={{ alignSelf: 'center', color: 'text.secondary' }} />
            <FormControl size={size} sx={{ minWidth: 180 }} disabled={disabled}>
              <InputLabel>Dieper</InputLabel>
              <Select
                value={selectedDeepFolder?.id || ''}
                label="Dieper"
                onChange={handleDeepFolderChange}
                MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
              >
                <MenuItem value="">
                  <em>Huidige map</em>
                </MenuItem>
                {deepFolders.map(deep => (
                  <MenuItem key={deep.id} value={deep.id}>
                    {deep.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Box>

      {/* Helper text */}
      {!selectedClient && (
        <Typography variant="caption" color="textSecondary">
          Selecteer een klant en projectmap, of schakel terug naar standaard locatie
        </Typography>
      )}
    </Box>
  );
};

export default FrameIoFolderPicker;
